import { useCallback, useState } from 'react';
import { useTranslation } from '@hooks/useTranslation';
import SendIcon from '@assets/icons/SendIcon';
import styles from './TagsInput.module.scss';

interface Props {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
}

export function TagsInput({ label, value, onChange, placeholder }: Props) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  const commit = useCallback(() => {
    const candidate = draft.trim();
    if (!candidate) return;
    if (value.some((v) => v.toLowerCase() === candidate.toLowerCase())) {
      setDraft('');
      return;
    }
    onChange([...value, candidate]);
    setDraft('');
  }, [draft, value, onChange]);

  const remove = useCallback(
    (idx: number) => {
      onChange(value.filter((_, i) => i !== idx));
    },
    [value, onChange],
  );

  return (
    <div className={styles.wrap}>
      <label className={styles.label}>{label}</label>
      <div className={styles.inputRow}>
        <input
          type="text"
          className={styles.input}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            }
          }}
          placeholder={placeholder}
          aria-label={label}
        />
        <button
          type="button"
          className={styles.commitBtn}
          onClick={commit}
          disabled={!draft.trim()}
          aria-label={t('K_BUTTON_ADD_TAG', 'Add tag')}
        >
          <SendIcon />
        </button>
      </div>
      <span className={styles.helper}>
        {t('K_HELPER_TAGS_INPUT', 'Press Enter to add')}
      </span>
      {value.length > 0 && (
        <div className={styles.chips}>
          {value.map((tag, idx) => (
            <span key={tag} className={styles.chip}>
              {tag}
              <button
                type="button"
                className={styles.chipRemove}
                onClick={() => remove(idx)}
                aria-label={t('K_REMOVE', 'Remove')}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default TagsInput;
